import { obtenerNombrePreguntaTipo, PreguntasTipoEnum } from "@shared/enums";
import { ItemPreguntaCargaDTO, PreguntaCargaDTO } from "@DTOS";
import { DatosPreguntaImportador } from "./DatosPreguntaImportador";
import { PREGUNTA_PADRE } from "./constantesImportador";
import { DatosAnteriores } from "./DatosAnteriores";
import { numeroRelativoRegExp, paisRegExp, replaceSubitemRegExp } from "@shared/utils";

export abstract class PreguntaImportador {
    protected errores: string[] = [];
    protected advertencias: string[] = [];
    protected datosAnteriores: DatosAnteriores;
    protected idsRepetidos: string[] = [];

    abstract puedeProcesar(tipoPregunta: PreguntasTipoEnum): boolean;

    public cargarDatosAnteriores(datosAnteriores: DatosAnteriores) {
        this.datosAnteriores = datosAnteriores;
    }

    public procesar(datos: DatosPreguntaImportador[]): PreguntaCargaDTO[] {
        return this.procesarPregunta(datos);
    }

    public testear(datos: DatosPreguntaImportador[], esUltima: boolean = false): boolean {
        let pasoTests = true;
        const preguntaPadre = datos[PREGUNTA_PADRE];
        if (this.preguntaDebeTenerItem(datos)) {
            pasoTests = false;
            this.errores.push(
                `La pregunta ${preguntaPadre.idPregunta} es de tipo ${obtenerNombrePreguntaTipo(preguntaPadre.tipo!)} y no tiene items`
            );
        }
        if (!this.datosAnteriores.idsPreguntasApuntadas.has(preguntaPadre.idPregunta)) {
            pasoTests = false;
            this.errores.push(
                `La pregunta ${preguntaPadre.idPregunta} no esta apuntada por ninguna.`
            );
        }
        pasoTests = this.verificarIdsRepetidos(datos) && pasoTests;
        pasoTests = this.preguntaSiguienteVaAtras(datos) && pasoTests;
        datos.forEach(datosPregunta => {
            pasoTests = this.preguntaSiguienteMayorA0(datosPregunta) && pasoTests;
            pasoTests = this.preguntaMayor400Caracteres(datosPregunta) && pasoTests;
            if (!esUltima) {
                pasoTests = this.verificarSiguiente(datosPregunta) && pasoTests;
            }
            pasoTests = this.preguntasTieneColumnaItemsVacio(datosPregunta) && pasoTests;
            pasoTests = this.itemsSoloKiller(datosPregunta) && pasoTests;
            if (datosPregunta.condiciones != null) {
                pasoTests = this.verificarCondicion(datosPregunta.condiciones, datosPregunta.idPregunta) && pasoTests;
                pasoTests = this.verificarCondicionMatrizApuntaPreguntaItem(
                    datosPregunta.condiciones, datosPregunta.idPregunta
                ) && pasoTests;
                if (preguntaPadre.tipo === PreguntasTipoEnum.FLOW_JUMP) {
                    pasoTests =
                        this.verificarCondicionApuntaPreguntaOItem(datosPregunta.condiciones, datosPregunta.idPregunta)
                        && pasoTests;
                } else {
                    pasoTests = this.verificarCondicionApuntaItem(
                        datosPregunta.condiciones, datosPregunta.idPregunta
                    ) && pasoTests;
                }
            }
        });

        return pasoTests;
    }

    public obtenerErrores(): string[] {
        const errores = this.errores;
        this.errores = [];
        return errores;
    }

    public obtenerAdvertencias(): string[] {
        const advertencias = this.advertencias;
        this.advertencias = [];
        return advertencias;
    }

    protected procesarPregunta(datos: DatosPreguntaImportador[]): PreguntaCargaDTO[] {
        const datosPreguntaPadre = datos[PREGUNTA_PADRE];
        if (datosPreguntaPadre.preguntaSiguiente != null) {
            this.datosAnteriores.idsPreguntasApuntadas.add(datosPreguntaPadre.preguntaSiguiente.toString());
        }
        const pregunta = this.generarPregunta(datosPreguntaPadre);
        const preguntas = [pregunta];
        datos.slice(1).forEach(datosPregunta => {
            if (datosPregunta.tipo == null) {
                const item = this.generarItem(datosPregunta);
                this.datosAnteriores.idsItems.push(item.id);
                pregunta.items.push(item);
            } else {
                const subpregunta = this.generarPregunta(datosPregunta);
                subpregunta.padre = pregunta.id;
                preguntas.push(subpregunta);
            }
        });
        return preguntas;
    }

    protected generarPregunta(datos: DatosPreguntaImportador): PreguntaCargaDTO {
        const pregunta = new PreguntaCargaDTO();
        pregunta.id = datos.idPregunta;
        pregunta.tipo = datos.tipo!;
        pregunta.texto = datos.texto ?? "";
        pregunta.aleatorio = datos.tipoOpcion == null ? (datos.random ?? null) :
            datos.tipoOpcion === "" ? null : datos.tipoOpcion === "R";
        pregunta.textoReferencia = datos.referencia ?? "";
        pregunta.siguientePregunta = datos.preguntaSiguiente ?? null;
        pregunta.condiciones = datos.condiciones ?? "";
        pregunta.minimo = datos.min ?? null;
        pregunta.maximo = datos.max ?? null;
        pregunta.paso = datos.step ?? null;
        pregunta.idCliente = datos.customerId ?? "";
        pregunta.idBloque = datos.bloqueId ?? null;
        pregunta.url = datos.item ?? "";
        pregunta.tieneImagen = this.puedeTenerImagen(datos.tipo!) && datos.item != null && datos.item !== "";
        return pregunta;
    }

    protected generarItem(datos: DatosPreguntaImportador): ItemPreguntaCargaDTO {
        const item = new ItemPreguntaCargaDTO();
        item.id = datos.idPregunta;
        item.texto = datos.item ?? "";
        item.preguntaSiguiente = datos.preguntaSiguiente ?? null;
        item.esOpcionKiller = datos.tipoOpcion == null ? (datos.killer ?? null) :
            datos.tipoOpcion === "" ? null : datos.tipoOpcion === "K";
        item.condiciones = datos.condiciones ?? "";
        item.clienteId = datos.customerId ?? "";
        item.alineacion = datos.tipoOpcion == null ? null :
            (["T", "B"].includes(datos.tipoOpcion) ? datos.tipoOpcion as "T" | "B" : null);
        item.url = datos.texto;
        return item;
    }

    private preguntaSiguienteMayorA0(datosPregunta: DatosPreguntaImportador): boolean {
        let pasoTest = true;
        if (datosPregunta.preguntaSiguiente != null) {
            if (datosPregunta.preguntaSiguiente <= 0) {
                this.errores.push(
                    `La columna ##Pregunta Siguiente no puede menor o igual a 0 en la pregunta ${datosPregunta.idPregunta}`
                );
                pasoTest = false;
            }
        }
        return pasoTest;
    }

    private preguntaMayor400Caracteres(datosPregunta: DatosPreguntaImportador): boolean {
        let pasoTest = true;
        if (datosPregunta.texto != null) {
            if (datosPregunta.texto.length > 400) {
                this.errores.push(`La pregunta en la fila ${datosPregunta.idPregunta} supera el límite de 400 caracteres`);
                pasoTest = false;
            }
        }

        return pasoTest;
    }

    private preguntasTieneColumnaItemsVacio(datosPregunta: DatosPreguntaImportador): boolean {
        let pasoTests = true;
        if (datosPregunta.tipo == null) {
            return pasoTests;
        }
        if (datosPregunta.item != null && datosPregunta.item !== "") {
            if (!datosPregunta.item.startsWith("https://")) {
                pasoTests = false;
                this.errores.push(`La pregunta ${datosPregunta.idPregunta} debe tener la columna ##Items vacia o debe ser un link`);
            }
            if (datosPregunta.tipo !== PreguntasTipoEnum.URL) {
                if (datosPregunta.tipo === PreguntasTipoEnum.VIDEO) {
                    if (!datosPregunta.item?.includes(".mp4")) {
                        pasoTests = false;
                        this.errores.push(`La pregunta ${datosPregunta.idPregunta} debe tener un link para un archivo mp4`);
                    }
                } else {
                    if (!datosPregunta.item?.includes(".jpg") && !datosPregunta.item?.includes(".jpeg")) {
                        pasoTests = false;
                        this.errores.push(`La pregunta ${datosPregunta.idPregunta} debe tener un link para un archivo jpg`);
                    }
                }
            }
        }
        return pasoTests;
    }

    private preguntaSiguienteVaAtras(datos: DatosPreguntaImportador[]): boolean {
        let pasoTests = true;
        const preguntaPadre = datos[PREGUNTA_PADRE];
        const idPreguntaPadre = parseInt(preguntaPadre.idPregunta, 10);
        datos.forEach(datosPregunta => {
            if (datosPregunta.preguntaSiguiente == null) {
                return;
            }
            if (datosPregunta.preguntaSiguiente === idPreguntaPadre && datosPregunta !== preguntaPadre) {
                pasoTests = false;
                this.errores.push(`En el item ${datosPregunta.idPregunta} tiene como siguiente la misma pregunta`);
            }
            if (datosPregunta.preguntaSiguiente < idPreguntaPadre &&
                preguntaPadre.tipo !== PreguntasTipoEnum.FLOW_JUMP) {
                pasoTests = false;
                this.errores.push(`'Pregunta Siguiente' está indicando un salto hacia atras en la pregunta ${datosPregunta.idPregunta}`);
            }
        });
        return pasoTests;
    }

    private preguntaDebeTenerItem(datos: DatosPreguntaImportador[]): boolean {
        const preguntaPadre = datos[PREGUNTA_PADRE];
        return preguntaPadre.tipo != null && !this.tiposPreguntasSinItems().includes(preguntaPadre.tipo) &&
            datos.every(datosPregunta => datosPregunta.item == null);
    }

    private itemsSoloKiller(datosPregunta: DatosPreguntaImportador): boolean {
        let pasoTests = true;
        if (datosPregunta.texto == null) {
            return pasoTests;
        }
        if (datosPregunta.killer == null) {
            return pasoTests;
        }
        pasoTests = false;
        this.errores.push(`En la pregunta ${datosPregunta.idPregunta} no puede tener la opcion Killer, solo los items`);
        return pasoTests;
    }

    private tiposPreguntasSinItems(): PreguntasTipoEnum[] {
        return [PreguntasTipoEnum.INFO, PreguntasTipoEnum.URL, PreguntasTipoEnum.PICTURE, PreguntasTipoEnum.FLOW_GROUP,
            PreguntasTipoEnum.TEXT, PreguntasTipoEnum.FLOW_CHECKPOINT, PreguntasTipoEnum.EXTRA_POINTS,
            PreguntasTipoEnum.NUMERIC_TEXT, PreguntasTipoEnum.NUMERIC_IN_RANGE, PreguntasTipoEnum.MATRIX_RANGE,
            PreguntasTipoEnum.VIDEO, PreguntasTipoEnum.DATE_SELECT];
    }

    private verificarCondicion(condicion: string, filaId: string): boolean {
        let condicionParseada = condicion.trim().replace(/~?\s*[0-9]+(?:\.[0-9]+)*/g, "true");
        condicionParseada = condicionParseada.replace(/~\s*/g, "");
        condicionParseada = this.controlPaisesExisten(condicionParseada, filaId);

        if (!this.parentesisBalanceados(condicionParseada)) {
            this.errores.push(
                `La columna ##Visible Si tiene sus parentesis mal balanceados en la fila ${filaId}`
            );

            return false;
        }

        condicionParseada = condicionParseada.replace(/\(\s*/g, "");
        condicionParseada = condicionParseada.replace(/\s*\)/g, "");

        if (!this.testearExpresionString(condicionParseada)) {
            this.errores.push(
                `La columna ##Visible Si tiene un error de sintaxis en la fila ${filaId}`
            );
            return false;
        }
        return true;
    }

    private verificarCondicionMatrizApuntaPreguntaItem(condicion: string, filaId: string): boolean {
        let pasoTests = true;
        let hayCondicion: any[] | null;
        while ((hayCondicion = numeroRelativoRegExp.exec(condicion)) != null) {
            const condicionEsMatriz = (hayCondicion[0].split(".").length - 1) === 2;
            if (!condicionEsMatriz) {
                continue;
            }
            if (this.datosAnteriores.idsItems.includes(condicionEsMatriz ?
                hayCondicion[0].replace(replaceSubitemRegExp, "$1.$2") : hayCondicion[0])
            ) {
                this.errores.push(
                    `La condicion ${hayCondicion[0]} de la fila ${filaId} apunta a un item que no existe`
                );
                pasoTests = false;
            }
        }
        return pasoTests;
    }

    private verificarCondicionApuntaItem(condicion: string, filaId: string): boolean {
        let pasoTests = true;
        let hayCondicion: any[] | null;
        while ((hayCondicion = numeroRelativoRegExp.exec(condicion)) != null) {
            const condicionEsMatriz = (hayCondicion[0].split(".").length - 1) === 2;
            if (!this.datosAnteriores.idsItems.includes(condicionEsMatriz ?
                hayCondicion[0].replace(replaceSubitemRegExp, "$1.$3") : hayCondicion[0])
            ) {
                this.errores.push(
                    `El item ${hayCondicion[0]} de la condicion de la pregunta ${filaId} no se encuentra antes`
                );
                pasoTests = false;
            }
        }
        return pasoTests;
    }

    private verificarCondicionApuntaPreguntaOItem(condicion: string, filaId: string): boolean {
        let pasoTests = true;
        let hayCondicion: any[] | null;
        while ((hayCondicion = numeroRelativoRegExp.exec(condicion)) != null) {
            const condicionSeparada = hayCondicion[0].split(".");
            if (condicionSeparada.length !== 1) {
                return this.verificarCondicionApuntaItem(condicion, filaId);
            }
            if (!this.datosAnteriores.idsPreguntasApuntadas.has(condicionSeparada[0])) {
                this.errores.push(
                    `La pregunta ${hayCondicion[0]} de la condicion de la pregunta ${filaId} no se encuentra antes`
                );
                pasoTests = false;
            }
        }
        return pasoTests;
    }

    private controlPaisesExisten(condicion: string, filaId: string): string {
        const paisesIdLista = this.datosAnteriores.paisesCodigos;
        let todoOk = true;
        let match;
        while ((match = paisRegExp.exec(condicion)) != null && todoOk) {
            if (!paisesIdLista.includes(match[1])) {
                this.errores.push(`El pais ${match[1]} en la pregunta ${filaId} no se encuentra en la lista de paises del BO`);
                todoOk = false;
            } else {
                const regex = new RegExp(`${match[1]}`);
                condicion = condicion.replace(regex, "true");
            }
        }
        return condicion;
    }

    private verificarIdsRepetidos(datos: DatosPreguntaImportador[]): boolean {
        let pasoTest = true;
        datos.forEach(datosPregunta => {
            const idPreguntaDuplicados = datos.filter(x => x.idPregunta === datosPregunta.idPregunta);
            if (idPreguntaDuplicados.length > 1 && !this.idsRepetidos.includes(datosPregunta.idPregunta)) {
                this.idsRepetidos.push(datosPregunta.idPregunta);
                this.errores.push(`El id ${datosPregunta.idPregunta} esta repetido ${idPreguntaDuplicados.length} veces`);
                pasoTest = false;
            }
        });
        return pasoTest;
    }

    private verificarSiguiente(datosPregunta: DatosPreguntaImportador): boolean {
        let pasoTest = true;
        if (datosPregunta.preguntaSiguiente == null) {
            this.errores.push(`En la pregunta ${datosPregunta.idPregunta} la columna pregunta siguiente no tiene valor`);
            pasoTest = false;
        }
        return pasoTest;
    }

    private parentesisBalanceados(str: string): boolean {
        return !str.split("").reduce((uptoPrevChar, thisChar) => {
            if (thisChar === "(" || thisChar === "{" || thisChar === "[") {
                return ++uptoPrevChar;
            } else if (thisChar === ")" || thisChar === "}" || thisChar === "]") {
                return --uptoPrevChar;
            }

            return uptoPrevChar;
        }, 0);
    }

    private testearExpresionString(expresion: string): boolean {
        const expresiones = {};
        const expresionRegex = new RegExp("\\((?:!*true|!*false|&|\\||\\s|!*\\w+)+\\)");
        const espaciosOperadores = new RegExp("(?<!\\s)([|&])(?!\\s)", "g");
        if (espaciosOperadores.test(expresion)) {
            expresion = expresion.replace(/\|/g, " | ").replace(/&/g, " & ");
        }

        let expresionIndex = 0;
        let matchArray = expresionRegex.exec(expresion);

        expresion = expresion.trim();

        while (matchArray) {
            let match = matchArray[0];
            const expression = "boolExpr" + expresionIndex;

            expresion = expresion.replace(match, expression);

            match = match.replace("(", "").replace(")", "");

            expresiones[expression] = match;
            expresionIndex++;

            matchArray = expresionRegex.exec(expresion);
        }

        return this.evaluarExpresionString(expresion, expresiones);
    }

    private evaluarExpresionString(expresion: string, expresiones: any): boolean {
        const condiciones = expresion.split(" ");

        if (condiciones.length > 0) {
            let condicionBooleano = this.aBooleano(condiciones[0], expresiones);

            for (let i = 1; i + 1 < condiciones.length; i += 2) {
                const comparador = condiciones[i];

                const valor = this.aBooleano(condiciones[i + 1], expresiones);

                switch (comparador) {
                case "&":
                    condicionBooleano = condicionBooleano && valor;
                    break;
                case "|":
                    condicionBooleano = condicionBooleano || valor;
                    break;
                }
            }

            return condicionBooleano;
        }

        return false;
    }

    private aBooleano(expresion: string, expresiones: any): boolean {
        let cantidadNegativos = 0;

        while (expresion.startsWith("!")) {
            expresion = expresion.replace("!", "");
            cantidadNegativos++;
        }

        let valorBooleano;

        if (expresion.startsWith("boolExpr")) {
            valorBooleano = this.evaluarExpresionString(expresiones[expresion], expresiones);
        } else if (expresion === "true" || expresion === "false") {
            valorBooleano = expresion === "true";
        } else {
            valorBooleano = false;
        }

        if (cantidadNegativos % 2 === 1) {
            valorBooleano = !valorBooleano;
        }

        return valorBooleano;
    }

    private puedeTenerImagen(tipo: PreguntasTipoEnum): boolean {
        return ![PreguntasTipoEnum.URL, PreguntasTipoEnum.VIDEO].includes(tipo);
    }
}
